// The organisation's Venues (spec/13 §13, F-ORG-01): every venue it runs, each with its status as a dot
// and its word, where it is and its timezone. New venue is the page's one primary action (a short
// stepper in a dialog, `CreateVenueDialog`); archiving a venue closes it for good, so it confirms and says so. A
// franchisor that may read its franchisees (`franchise:read`, ADR-0019 Phase 2) also sees their venues,
// read-only, below its own.

import type { Franchisee, Venue } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Button, EmptyState, Skeleton, useToast } from '@clavian/ui-web';
import { Link } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';

import { useAccess, useMyVenues } from '../../api/queries';
import { LoadFailure } from '../../shell/LoadFailure';
import { Icon, NavIcon } from '../../shell/nav-icons';
import { useScopeIds } from '../../shell/scope';
import { CardsSkeleton, Dot, Forbidden, Notice, Page, RowActions, Section } from '../../ui';
import { CreateVenueDialog } from './CreateVenueDialog';
import { useArchiveVenue, useFranchisees, useFranchiseeVenues, useOrganizationVenues } from './queries';

import './venues.css';
import { useProblem } from '../../api/use-problem';

const VENUES_READ = 'venues:read';
const VENUES_CREATE = 'venues:create';
const VENUES_ARCHIVE = 'venues:archive';
const FRANCHISE_READ = 'franchise:read';

const STATUS_TONE: Readonly<Record<Venue['status'], 'ok' | 'warn' | 'neutral' | 'danger'>> = {
  live: 'ok',
  setup: 'neutral',
  paused: 'warn',
  closed: 'danger',
};

const VENUE_RAIL: Readonly<Record<Venue['status'], 'ok' | 'warn' | 'off' | undefined>> = {
  live: 'ok',
  setup: undefined,
  paused: 'warn',
  closed: 'off',
};

export function VenuesPage(): ReactNode {
  const { t } = useTranslation('console');
  const scope = useScopeIds();
  const access = useAccess(scope.access);
  const ready = access.status === 'ready';
  const canRead = ready && access.can(VENUES_READ);
  const canCreate = ready && access.can(VENUES_CREATE);
  const canArchive = ready && access.can(VENUES_ARCHIVE);
  const canFranchise = ready && access.can(FRANCHISE_READ);
  const venues = useOrganizationVenues(scope.organizationId, canRead);
  const [creating, setCreating] = useState(false);
  const [archiving, setArchiving] = useState<Venue | null>(null);

  if (ready && !canRead) {
    return (
      <Page title={t('venues.title')}>
        <Forbidden message={t('venues.notPermitted')} />
      </Page>
    );
  }

  const actions = canCreate ? (
    <Button variant="primary" icon={<Icon name="plus" />} onClick={() => setCreating(true)}>
      {t('venues.new')}
    </Button>
  ) : undefined;

  return (
    <Page title={t('venues.title')} description={t('venues.intro')} actions={actions}>
      {venues.data === undefined ? (
        venues.isError ? (
          <LoadFailure error={venues.error} message={t('venues.unavailable')} onRetry={() => void venues.refetch()} />
        ) : (
          <CardsSkeleton count={3} label={t('app.loading')} />
        )
      ) : venues.data.length === 0 ? (
        <EmptyState icon={<NavIcon name="venues" />} title={t('venues.emptyTitle')} description={canCreate ? t('venues.empty') : t('venues.emptyReadOnly')} />
      ) : (
        <ul className="console-venues" aria-label={t('venues.listLabel')}>
          {venues.data.map((venue) => (
            <li key={venue.id}>
              <VenueCard venue={venue} {...(canArchive ? { onArchive: () => setArchiving(venue) } : {})} />
            </li>
          ))}
        </ul>
      )}

      {archiving === null ? null : <ArchiveConfirm venue={archiving} onDone={() => setArchiving(null)} />}

      {canFranchise ? <FranchiseeVenues organizationId={scope.organizationId} /> : null}

      <CreateVenueDialog organizationId={scope.organizationId} open={creating} onClose={() => setCreating(false)} />
    </Page>
  );
}

function VenueCard({ venue, onArchive }: { readonly venue: Venue; readonly onArchive?: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const mine = useMyVenues().data?.some((one) => one.id === venue.id) === true;
  const where = venue.address === null ? t('venues.noAddress') : [venue.address.locality, venue.address.region].join(', ');
  return (
    // The venue's state as the rail along its card's top (ADR-0043): green live, warn paused, hatched closed.
    <article className="console-venue-card console-rail" data-rail={VENUE_RAIL[venue.status]} aria-label={venue.name}>
      <div className="console-venue-card__head">
        <h2 className="console-venue-card__name">{venue.name}</h2>
        {onArchive === undefined ? null : <RowActions name={venue.name} items={[{ id: 'archive', label: t('venues.archive'), onSelect: onArchive }]} />}
      </div>
      <Dot tone={STATUS_TONE[venue.status]}>{t(`venues.status.${venue.status}`)}</Dot>
      <span className="console-muted">{where}</span>
      <span className="console-subtle">
        {venue.timezone} · <span className="console-num">{venue.currency}</span>
      </span>
      {mine ? (
        <Link className="console-venue-card__open" to="/v/$venueSlug" params={{ venueSlug: venue.slug }}>
          {t('venues.open')}
        </Link>
      ) : null}
    </article>
  );
}

function ArchiveConfirm({ venue, onDone }: { readonly venue: Venue; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const scope = useScopeIds();
  const archive = useArchiveVenue(scope.organizationId);
  return (
    <Notice
      tone="warn"
      role="alert"
      title={t('venues.archiveConfirm', { name: venue.name })}
      action={
        <>
          <Button variant="ghost" size="sm" onClick={onDone}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="danger"
            className="clav-button--danger-solid"
            size="sm"
            busy={archive.isPending}
            onClick={() =>
              archive.mutate(venue.id, {
                onSuccess: () => {
                  show({ tone: 'ok', title: t('venues.archived', { name: venue.name }) });
                  onDone();
                },
                onError: (failure) => show({ tone: 'danger', title: problem.text(failure, { fallback: t('venues.archiveFailed') }) }),
              })
            }
          >
            {t('venues.archive')}
          </Button>
        </>
      }
    />
  );
}

function FranchiseeVenues({ organizationId }: { readonly organizationId: string }): ReactNode {
  const { t } = useTranslation('console');
  const franchisees = useFranchisees(organizationId, true);
  // An organisation with no franchisees has nothing to show here, so the section stays away.
  if (franchisees.data !== undefined && franchisees.data.length === 0) return null;
  return (
    <Section title={t('venues.franchisees.title')} description={t('venues.franchisees.hint')}>
      {franchisees.data === undefined ? (
        franchisees.isError ? (
          <LoadFailure error={franchisees.error} message={t('venues.franchisees.unavailable')} onRetry={() => void franchisees.refetch()} />
        ) : (
          <Skeleton shape="text" lines={2} />
        )
      ) : (
        <div className="console-venues__franchisees">
          {franchisees.data.map((franchisee) => (
            <FranchiseeGroup key={franchisee.id} organizationId={organizationId} franchisee={franchisee} />
          ))}
        </div>
      )}
    </Section>
  );
}

function FranchiseeGroup({ organizationId, franchisee }: { readonly organizationId: string; readonly franchisee: Franchisee }): ReactNode {
  const { t } = useTranslation('console');
  const venues = useFranchiseeVenues(organizationId, franchisee.id);
  return (
    <div className="console-venues__franchisee">
      <h3 className="console-venues__franchisee-name">{franchisee.name}</h3>
      {venues.data === undefined ? (
        <Skeleton shape="text" lines={1} />
      ) : venues.data.length === 0 ? (
        <p className="console-subtle">{t('venues.franchisees.none')}</p>
      ) : (
        <ul className="console-venues" aria-label={t('venues.franchisees.listLabel', { name: franchisee.name })}>
          {venues.data.map((venue) => (
            <li key={venue.id}>
              <VenueCard venue={venue} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
